import { admin2 } from '@/api/instance'

/**
 * 推薦服務搭配設定（CNCY-10772）
 *
 * 服務的格式為 `{ type, id }`，`type` 為 `service`（服務項目）或 `serviceAttach`（附加服務）
 */

const baseUrl = (shopId) => `/${shopId}/appointmentServicePairing`

/**
 * 取得推薦服務搭配列表，依排序值顯示
 *
 * @param {Object} params - 查詢參數
 * @param {string} params.shopId - 店家 ID
 * @param {number} [params.start=0] - 起始位置
 * @param {number} [params.limit] - 取得筆數
 * @returns {import('@/api/instance').Api2Response<Array>} 搭配設定列表
 * @example
 * const [res, err] = await GetAppointmentServicePairing({ shopId, start: 0, limit: 10 })
 * // res = [{
 * //   id, order,
 * //   mainService: { type: 'service', id, name: '染髮', isPublic: true },
 * //   pairedServices: [{ type: 'serviceAttach', id, name: '深層護髮', isPublic: false }],
 * // }]
 */
export const GetAppointmentServicePairing = async ({ shopId, start, limit }) => {
  return await admin2({
    method: 'GET',
    url: baseUrl(shopId),
    params: { start, limit },
  })
}

/**
 * 取得推薦服務搭配數量
 *
 * @param {Object} params - 查詢參數
 * @param {string} params.shopId - 店家 ID
 * @returns {import('@/api/instance').Api2Response<{ count: number }>} 數量
 */
export const GetAppointmentServicePairingCount = async ({ shopId }) => {
  return await admin2({
    method: 'GET',
    url: `${baseUrl(shopId)}/count`,
  })
}

/**
 * 取得單一推薦服務搭配
 *
 * @param {Object} params - 查詢參數
 * @param {string} params.shopId - 店家 ID
 * @param {string} params.id - 搭配設定 ID
 * @returns {import('@/api/instance').Api2Response<Object>} 搭配設定
 */
export const FindAppointmentServicePairing = async ({ shopId, id }) => {
  return await admin2({
    method: 'GET',
    url: `${baseUrl(shopId)}/${id}`,
  })
}

/**
 * 新增推薦服務搭配
 *
 * @param {Object} params - 參數
 * @param {string} params.shopId - 店家 ID
 * @param {{ type: string, id: string }} params.mainService - 主要服務
 * @param {Array<{ type: string, id: string }>} params.pairedServices - 搭配服務（1～10 個）
 * @param {number} [params.order=100] - 排序
 * @returns {import('@/api/instance').Api2Response<Object>} 新增的搭配設定
 */
export const CreateAppointmentServicePairing = async ({ shopId, mainService, pairedServices, order }) => {
  return await admin2({
    method: 'POST',
    url: baseUrl(shopId),
    data: { mainService, pairedServices, order },
  })
}

/**
 * 更新推薦服務搭配；搭配服務會整批取代
 *
 * @param {Object} params - 參數
 * @param {string} params.shopId - 店家 ID
 * @param {string} params.id - 搭配設定 ID
 * @param {{ type: string, id: string }} params.mainService - 主要服務
 * @param {Array<{ type: string, id: string }>} params.pairedServices - 搭配服務（1～10 個）
 * @param {number} [params.order] - 排序
 * @returns {import('@/api/instance').Api2Response<Object>} 更新後的搭配設定
 */
export const UpdateAppointmentServicePairing = async ({ shopId, id, mainService, pairedServices, order }) => {
  return await admin2({
    method: 'PUT',
    url: `${baseUrl(shopId)}/${id}`,
    data: { mainService, pairedServices, order },
  })
}

/**
 * 刪除推薦服務搭配
 *
 * @param {Object} params - 參數
 * @param {string} params.shopId - 店家 ID
 * @param {string} params.id - 搭配設定 ID
 * @returns {import('@/api/instance').Api2Response<Object>} 刪除的搭配設定
 */
export const DeleteAppointmentServicePairing = async ({ shopId, id }) => {
  return await admin2({
    method: 'DELETE',
    url: `${baseUrl(shopId)}/${id}`,
  })
}
